import React from 'react';
import { Helmet } from 'react-helmet';
import { DEFAULT_OG_IMAGE, PAGE_OG_IMAGES, SITE_NAME, SITE_URL } from '../constants/seo';

type SeoProps = {
  /** ≤ 60 characters ideally. Brand suffix is appended automatically unless `noSuffix`. */
  title: string;
  /** 120–155 characters ideally. */
  description: string;
  /** Route path starting with "/". Used for canonical + og:url. */
  path: string;
  image?: string;
  noindex?: boolean;
  noSuffix?: boolean;
  /** One or more JSON-LD objects. */
  schema?: Record<string, unknown> | Record<string, unknown>[];
};

const Seo: React.FC<SeoProps> = ({ title, description, path, image, noindex, noSuffix, schema }) => {
  const canonical = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
  const fullTitle = noSuffix ? title : `${title} | ${SITE_NAME}`;
  const ogImage = image ?? PAGE_OG_IMAGES[path] ?? DEFAULT_OG_IMAGE;
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <html lang="en-GB" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
